import { useEffect, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';
import { SectionBox } from '@/components';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { complianceAlertStatsActions } from '@/redux/actions';
import {
  complianceAlertStatsSelectors,
  showDemoDataSelectors,
} from '@/redux/selectors';

import { alertsDemoData } from '../../Alerts.constants';
import styles from './Stats.module.sass';

type StatsItemType = {
  title: string;
  value: number;
};

const Item = ({ title, value }: StatsItemType) => {
  return (
    <div key={title} className={styles.item}>
      <div className={styles.title}>{title}</div>
      <NumericFormat
        value={value}
        displayType="text"
        thousandSeparator=" "
        className={styles.value}
      />
    </div>
  );
};

const Stats = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: alertStatsData } = useAppSelector(
    complianceAlertStatsSelectors.getComplianceAlertStatsSlice,
  );

  const isShowDemoData = useAppSelector(showDemoDataSelectors.byCompliance);

  useEffect(() => {
    if (!isShowDemoData) {
      dispatch(complianceAlertStatsActions.fetchAlertStats());
    }
  }, [isShowDemoData]);

  const listData = useMemo(() => {
    const data = isShowDemoData ? alertsDemoData : alertStatsData;
    const alertStats = data?.stats;
    const alertsTotal = data?.alerts?.total || 0;

    return {
      total: {
        title: t('translation.total'),
        value: alertsTotal,
      },
      byRisk: [
        {
          title: t('translation.risk_explanation_level_low'),
          value: alertStats?.byRisk?.low || 0,
        },
        {
          title: t('translation.risk_explanation_level_middle'),
          value: alertStats?.byRisk?.medium || 0,
        },
        {
          title: t('translation.risk_explanation_level_high'),
          value: alertStats?.byRisk?.high || 0,
        },
        {
          title: t('translation.severe_risk'),
          value: alertStats?.byRisk?.severe || 0,
        },
      ],
      byStatus: [
        {
          title: t('translation.new'),
          value: alertStats?.byStatus?.new || 0,
        },
        {
          title: t('translation.pending'),
          value: alertStats?.byStatus?.pending || 0,
        },
        {
          title: t('translation.confirmed'),
          value: alertStats?.byStatus?.confirmed || 0,
        },
        {
          title: t('translation.rejected'),
          value: alertStats?.byStatus?.rejected || 0,
        },
      ],
    };
  }, [isShowDemoData, alertStatsData]);

  return (
    <SectionBox>
      {{
        body: (
          <div className={styles.stats}>
            <div className={styles.total}>
              <Item title={listData.total.title} value={listData.total.value} />
            </div>

            <div className={styles.list}>
              <div className={styles.first}>
                <div className={styles.subtitle}>
                  {t('translation.by_risk')}:
                </div>
                <div className={styles.items}>
                  {listData.byRisk.map((item) => (
                    <Item
                      key={item.title}
                      title={item.title}
                      value={item.value}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.second}>
                <div className={styles.subtitle}>
                  {t('translation.by_status')}:
                </div>
                <div className={styles.items}>
                  {listData.byStatus.map((item) => (
                    <Item
                      key={item.title}
                      title={item.title}
                      value={item.value}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        ),
      }}
    </SectionBox>
  );
};

export default Stats;
